/* eslint-disable @typescript-eslint/no-unused-vars, eqeqeq */
import { FC, Fragment, useEffect, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import CheckboxAsRadio from "shared/Checkbox/CheckboxAsRadio";
import ButtonSubmit from "./ButtonSubmit";
import { PathName } from "routers/types";
import { useDispatch, useSelector } from "react-redux";

import { setStay } from "store/actions/stay";
import { setStayCurrentPrice } from "store/actions/stayCurrentPrice";
import { setStayCurrentCharges } from "store/actions/stayCurrentCharges";
import { setStayCurrentNON } from "store/actions/stayCurrentNON";
import { setStayCurrentTotalPrice } from "store/actions/stayCurrentTotalPrice";
import { setStayCurrentTotalPriceWithCharges } from "store/actions/stayCurrentTotalPriceWithCharges";
import { setStayCancellationTypes } from "store/actions/stayCancellationTypes";

export interface CancellationInputProps {
  fieldClassName?: string;
  className?: string;
  buttonSubmitHref?: PathName;
  hasButtonSubmit?: boolean;
  isDisabled?: boolean;
  defaultFocus?: boolean;
  setSelectedCancelation: (v: string) => void;
  data?: any; // current hotel object (for .markup)
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

// 0 → strict / non-refundable | 2 → flexible / refundable
const classifyCancellation = (expiry: string | null | undefined): 0 | 2 =>
  expiry === null || expiry === "" || expiry === undefined ? 0 : 2;

/** symbolic id we keep in Redux (null|`flexible`) */
const cancellationBucket = (expiry: string | null | undefined) =>
  classifyCancellation(expiry) === 0 ? null : "flexible";

// map bucket index to human-readable label
const LABELS: Record<0 | 2, string> = {
  0: "Strict",
  2: "Flexible",
};

/** apply hotel markup once */
const withMarkup = (n: number, markup?: number) =>
  markup && markup > 0 ? n * (1 + markup / 100) : n;

/* ------------------------------------------------------------------ */
/* Component                                                          */
/* ------------------------------------------------------------------ */

const CancellationInput: FC<CancellationInputProps> = ({
  fieldClassName = "nc-hero-field-padding",
  className = "[ nc-flex-1 ]",
  buttonSubmitHref = "/listing-stay-map",
  hasButtonSubmit = true,
  isDisabled = false,
  defaultFocus = false,
  setSelectedCancelation,
  data = {},
}) => {
  const dispatch = useDispatch();

  /* redux ---------------------------------------------------------------- */

  const stay = useSelector((s: any) => s.stay.stay) ?? [];
  const mealTypesRedux =
    useSelector((s: any) => s.stayMealTypes.stayMealTypes) ?? [];
  const cancellationTypesRedux =
    useSelector((s: any) => s.stayCancellationTypes.stayCancellationTypes) ??
    [];

  /* local ui state ------------------------------------------------------- */

  const [selectedValue, setSelectedValue] = useState<string>(() => {
    const initial = cancellationTypesRedux.find((c: any) => c.checked)?.name;
    return initial ?? "Strict";
  });
  const [focusedInput, setFocusedInput] = useState(defaultFocus);

  /* build unique cancellation buckets once -------------------------------- */

  useEffect(() => {
    if (!stay.length || stay[0].no_availability) {
      return;
    }

    const buckets: any[] = [];

    stay.forEach((rate: any) => {
      const kind = classifyCancellation(rate.cancellation_expiry); // 0|2
      const name = LABELS[kind];
      if (!buckets.some((b) => b.name === name)) {
        buckets.push({
          id: rate.id,
          name,
          description: "",
          checked: false,
          cancellationId: cancellationBucket(rate.cancellation_expiry), // null | "flexible"
        });
      }
    });

    if (!buckets.length) {
      buckets.push({
        id: 0,
        name: "Strict",
        checked: true,
        description: "",
        cancellationId: null,
      });
    } else {
      buckets[0].checked = true;
    }

    dispatch(setStayCancellationTypes(buckets));
    setSelectedValue(buckets[0].name);
    setSelectedCancelation(buckets[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stay.length]);

  /* push chosen rate’s prices to redux ----------------------------------- */

  const pushPrices = (rate: any) => {
    const markup = data?.markup;
    dispatch(setStayCurrentPrice(withMarkup(rate.price, markup)));

    const rawCharges =
      (rate.totalPriceWithCharges ?? 0) - (rate.totalPrice ?? 0);
    dispatch(setStayCurrentCharges(withMarkup(rawCharges, markup)));
    dispatch(setStayCurrentNON(rate.numberOfNights));
    dispatch(setStayCurrentTotalPrice(withMarkup(rate.totalPrice, markup)));
    dispatch(
      setStayCurrentTotalPriceWithCharges(
        withMarkup(rate.totalPriceWithCharges, markup)
      )
    );
  };

  /* predicate helpers ---------------------------------------------------- */

  const matchesBucket = (rate: any, bucketId: string | null) => {
    const expiry = rate.cancellation_expiry;
    // bucketId null means strict (no cancellation)
    // expiry should be null, undefined, or empty string
    if (bucketId === null) {
      return expiry === null || expiry === undefined || expiry === "";
    }
    // bucketId "flexible" means has cancellation expiry
    return expiry !== null && expiry !== undefined && expiry !== "";
  };

  /* on user change ------------------------------------------------------- */

  const handleChange = (name: string, bucketId: string | null) => {
    setSelectedValue(name);
    setSelectedCancelation(name);

    const updatedBuckets = cancellationTypesRedux.map((c: any) =>
      c.cancellationId === bucketId
        ? { ...c, checked: true }
        : { ...c, checked: false }
    );
    dispatch(setStayCancellationTypes(updatedBuckets));

    /* current selected meal board -------------------------------------- */
    const selectedBoardId = mealTypesRedux.find((m: any) => m.checked)?.boardId;

    const candidates = stay.filter(
      (r: any) =>
        r.board === selectedBoardId &&
        !r.no_availability &&
        matchesBucket(r, bucketId)
    );

    const lowest = candidates.reduce(
      (lo: any, r: any) => (lo === null || +r.price < +lo.price ? r : lo),
      null
    );

    if (!lowest) {
      return;
    }

    const marked = stay.map((r: any) =>
      r.id === lowest.id ? { ...r, checked: true } : { ...r, checked: false }
    );
    dispatch(setStay(marked));
    pushPrices(lowest);
  };

  /* ------------------------------------------------------------------ */
  /* render                                                             */
  /* ------------------------------------------------------------------ */

  return (
    <Popover className={`flex relative ${className}`}>
      {({ open }) => (
        <>
          <div
            className={`flex-1 flex items-center cursor-pointer ${
              open ? "nc-hero-field-focused" : ""
            }`}
          >
            <Popover.Button
              className={`flex-1 flex text-left items-center ${fieldClassName} space-x-3`}
            >
              <div className="text-neutral-300 dark:text-neutral-400">
                <i className="la la-clipboard-list text-4xl" />
              </div>
              <div className="flex-grow">
                <span className="block xl:text-lg font-semibold">
                  {selectedValue}
                </span>
                <span className="block mt-1 text-sm text-neutral-400 leading-none font-light">
                  Cancellation
                </span>
              </div>

              {hasButtonSubmit && (
                <div className="pr-2 xl:pr-4">
                  <ButtonSubmit href={buttonSubmitHref} />
                </div>
              )}
            </Popover.Button>
          </div>

          {!isDisabled && (
            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute right-0 z-10 w-max max-w-sm bg-white dark:bg-neutral-800 top-full mt-3 py-6 px-8 rounded-3xl shadow-xl">
                <div className="flex flex-col space-y-5">
                  {cancellationTypesRedux.map((item: any) => {
                    console.log("Rendering cancellation option:", item);
                    return (
                      <CheckboxAsRadio
                        key={item.name}
                        name={item.name}
                        label={item.name}
                        value={String(item.id)}
                        checked={item.checked}
                        onChange={() => {
                          console.log(
                            "CheckboxAsRadio onChange triggered for:",
                            item.name
                          );
                          handleChange(item.name, item.cancellationId);
                        }}
                      />
                    );
                  })}
                </div>
              </Popover.Panel>
            </Transition>
          )}
        </>
      )}
    </Popover>
  );
};

export default CancellationInput;
